/**
 * Report Builder - Place Patient Story JavaScript
 *
 * Manages patient story placement on the report canvas
 */

(function () {
  "use strict";

  // Store placed patient stories
  let placedPatientStories = [];
  let patientStoryCounter = 0;
  let isAddingPatientStoryPlaceholder = false;

  /**
   * Initialize the place patient story functionality
   */
  function initPlacePatientStory() {
    // This will be called from the toolbar button click
  }

  /**
   * Add patient story placeholder to canvas with inline form
   */
  function addPatientStoryPlaceholder() {
    // Prevent duplicate calls
    if (isAddingPatientStoryPlaceholder) {
      return;
    }

    isAddingPatientStoryPlaceholder = true;

    const canvasCharts = window.ReportBuilderUtils?.getCanvasContainer();
    if (!canvasCharts) {
      isAddingPatientStoryPlaceholder = false;
      return;
    }

    // Hide empty message
    if (window.ReportBuilderUtils?.toggleEmptyMessage) {
      window.ReportBuilderUtils.toggleEmptyMessage(false);
    }

    // Create placeholder container
    const placeholderContainer = document.createElement("div");
    placeholderContainer.className = "canvas-patient-story-placeholder";
    placeholderContainer.id = `patient_story_placeholder_${Date.now()}`;

    // Clone the inline form template
    const inlineFormTemplate = document.querySelector(
      ".report-builder-place-patient-story-inline"
    );
    if (!inlineFormTemplate) {
      console.error("Inline patient story form template not found");
      isAddingPatientStoryPlaceholder = false;
      return;
    }

    const inlineForm = inlineFormTemplate.cloneNode(true);
    inlineForm.style.display = "block";
    inlineForm.id = `inline_patient_story_form_${Date.now()}`;

    // Update IDs to be unique
    const selectPatientStory = inlineForm.querySelector("#inlineSelectPatientStory");
    const addPatientStoryBtn = inlineForm.querySelector("#inlineAddPatientStoryBtn");

    const uniqueId = Date.now();

    if (selectPatientStory) {
      selectPatientStory.id = `inlineSelectPatientStory_${uniqueId}`;
    }
    if (addPatientStoryBtn) {
      addPatientStoryBtn.id = `inlineAddPatientStoryBtn_${uniqueId}`;
    }

    placeholderContainer.appendChild(inlineForm);
    canvasCharts.appendChild(placeholderContainer);

    // Initialize form handlers for this instance
    initInlinePatientStoryFormInstance(inlineForm, placeholderContainer);

    // Scroll to the new placeholder
    placeholderContainer.scrollIntoView({
      behavior: "smooth",
      block: "nearest",
    });

    // Reset flag after a short delay
    setTimeout(function () {
      isAddingPatientStoryPlaceholder = false;
    }, 100);
  }

  /**
   * Initialize inline patient story form instance
   */
  function initInlinePatientStoryFormInstance(
    formElement,
    placeholderContainer
  ) {
    const selectPatientStory = formElement.querySelector(
      "select[id^='inlineSelectPatientStory']"
    );
    const addPatientStoryBtn = formElement.querySelector(
      "button[id^='inlineAddPatientStoryBtn']"
    );
    const cancelButtons = formElement.querySelectorAll(
      ".btn-cancel-patient-story-config"
    );

    if (!selectPatientStory || !addPatientStoryBtn) {
      return;
    }

    // Handle patient story selection
    selectPatientStory.addEventListener("change", function () {
      const hasPatientStory = selectPatientStory.value !== "";
      addPatientStoryBtn.disabled = !hasPatientStory;
    });

    // Handle add patient story button
    addPatientStoryBtn.addEventListener("click", function () {
      addPatientStoryFromInlineFormInstance(formElement, placeholderContainer);
    });

    // Handle cancel buttons
    cancelButtons.forEach((btn) => {
      btn.addEventListener("click", function () {
        placeholderContainer.remove();

        // Show empty message if no items left
        const canvasCharts = window.ReportBuilderUtils?.getCanvasContainer();
        if (canvasCharts && canvasCharts.children.length === 0) {
          if (window.ReportBuilderUtils?.toggleEmptyMessage) {
            window.ReportBuilderUtils.toggleEmptyMessage(true);
          }
        }
      });
    });
  }

  /**
   * Add patient story from inline form instance
   */
  function addPatientStoryFromInlineFormInstance(
    formElement,
    placeholderContainer
  ) {
    const selectPatientStory = formElement.querySelector(
      "select[id^='inlineSelectPatientStory']"
    );

    if (!selectPatientStory || !selectPatientStory.value) {
      return;
    }

    const selectedOption = selectPatientStory.options[selectPatientStory.selectedIndex];
    const storyId = parseInt(selectPatientStory.value);
    const storyTitle =
      selectedOption.getAttribute("data-story-title") ||
      selectedOption.textContent;
    const storyExcerpt =
      selectedOption.getAttribute("data-story-excerpt") || "";
    const qrCodeUrl =
      selectedOption.getAttribute("data-story-qr-code") || "";
    const featuredImageUrl =
      selectedOption.getAttribute("data-story-featured-image") || "";
    const storyUrl = selectedOption.getAttribute("data-story-url") || "";

    // Create unique patient story ID
    const patientStoryItemId = `report_patient_story_${++patientStoryCounter}_${Date.now()}`;

    // Create patient story configuration
    const patientStoryConfig = {
      id: patientStoryItemId,
      storyPostId: storyId,
      title: storyTitle,
      excerpt: storyExcerpt,
      qrCodeUrl: qrCodeUrl,
      featuredImageUrl: featuredImageUrl,
      storyUrl: storyUrl,
      customTitle: null, // Will default to patient story title if null
    };

    // Add to placed patient stories
    placedPatientStories.push(patientStoryConfig);

    // Replace placeholder with patient story
    renderPatientStoryInCanvas(patientStoryConfig, placeholderContainer);
  }

  /**
   * Wrap the patient story title text in a link to the story (opens in a new tab)
   */
  function wrapPatientStoryTitleLink(container, patientStoryConfig) {
    if (!patientStoryConfig.storyUrl) {
      return;
    }

    const titleEl = container.querySelector(
      ".patient-story-item-title .title-text"
    );
    if (!titleEl) {
      return;
    }

    const titleText = titleEl.textContent;
    if (!titleText.trim()) {
      return;
    }

    const titleLink = document.createElement("a");
    titleLink.className = "patient-story-title-link";
    titleLink.href = patientStoryConfig.storyUrl;
    titleLink.target = "_blank";
    titleLink.rel = "noopener noreferrer";
    titleLink.title = "Open patient story in a new tab";
    titleLink.textContent = titleText;

    titleEl.textContent = "";
    titleEl.appendChild(titleLink);
  }

  // px equivalents of the PDF export's media row constants, so the canvas
  // preview and the generated report lay the row out the same way.
  const PS_MEDIA_MAX_HEIGHT = 260; // caps the row height
  const PS_MEDIA_GAP = 32; // the 2rem gap between the image and the QR

  /**
   * Draw the featured image and the QR code at the same height (MCH-211).
   *
   * CSS can't do this alone: the QR is an uploaded image of any pixel size, so
   * left to size itself it would drive the row height rather than follow the
   * photo. The height is derived from the row's width the same way the PDF
   * export derives it (see patientStoryMediaLayout in chart-pdf-export.js), and
   * the square ratio in the SCSS turns that height into the QR's width.
   *
   * Measuring the row rather than the photo matters: setting the heights can't
   * change the row's width, so there's no feedback loop to oscillate in.
   */
  function sizePatientStoryMedia(container) {
    const media = container.querySelector(".patient-story-media");
    if (!media) {
      return;
    }

    const imageEl = media.querySelector(".patient-story-featured-image img");
    const qrEl = media.querySelector(".patient-story-qr-code");
    // Only one of the two — the CSS already sizes those on its own
    if (!imageEl || !qrEl) {
      return;
    }

    function sync() {
      const available = media.clientWidth;
      const ratio = imageEl.naturalWidth / imageEl.naturalHeight;
      if (!available || !ratio || !isFinite(ratio)) {
        return;
      }

      // QR codes are square, so it takes up as much width as the row is tall
      const widthLimited = Math.max(0, available - PS_MEDIA_GAP) / (ratio + 1);
      const height = Math.min(
        PS_MEDIA_MAX_HEIGHT,
        widthLimited,
        // Never blow a small photo up past its own size
        imageEl.naturalHeight
      );

      imageEl.style.height = height + "px";
      qrEl.style.height = height + "px";
    }

    if (imageEl.complete && imageEl.naturalHeight) {
      sync();
    } else {
      imageEl.addEventListener("load", sync);
    }

    // The row narrows with the canvas, which lowers the shared height
    if (typeof ResizeObserver === "function") {
      new ResizeObserver(sync).observe(media);
    } else {
      window.addEventListener("resize", sync);
    }
  }

  /**
   * Render patient story in canvas
   */
  function renderPatientStoryInCanvas(patientStoryConfig, placeholder = null) {
    const canvasCharts = window.ReportBuilderUtils?.getCanvasContainer();
    if (!canvasCharts) {
      return;
    }

    // Create patient story container
    const patientStoryContainer = document.createElement("div");
    patientStoryContainer.className = "canvas-patient-story-item";
    patientStoryContainer.id = `patient_story_container_${patientStoryConfig.id}`;
    patientStoryContainer.setAttribute(
      "data-patient-story-id",
      patientStoryConfig.id
    );
    if (patientStoryConfig.storyUrl) {
      patientStoryContainer.setAttribute(
        "data-story-url",
        patientStoryConfig.storyUrl
      );
    }

    // Create patient story header with controls
    const patientStoryHeader = document.createElement("div");
    patientStoryHeader.className = "patient-story-item-header";
    const displayTitle = patientStoryConfig.customTitle || patientStoryConfig.title;

    if (window.ReportBuilderUtils?.generateItemHeaderHtml) {
      patientStoryHeader.innerHTML =
        window.ReportBuilderUtils.generateItemHeaderHtml({
          itemId: patientStoryConfig.id,
          displayTitle: displayTitle,
          idAttribute: "data-patient-story-id",
          itemType: "patient-story",
          removeButtonClass: "btn-remove-patient-story",
          removeButtonTitle: "Remove patient story",
        });
    } else {
      // Fallback if utils not available
      patientStoryHeader.innerHTML = `<div class="patient-story-item-title"><h4 class="title-text">${displayTitle}</h4></div>`;
    }

    // Link the title to the full story
    wrapPatientStoryTitleLink(patientStoryHeader, patientStoryConfig);

    // Create patient story content area
    const patientStoryContent = document.createElement("div");
    patientStoryContent.className = "patient-story-item-content";

    const escapeHtml = window.ReportBuilderUtils?.escapeHtml || function(text) {
      const div = document.createElement("div");
      div.textContent = text;
      return div.innerHTML;
    };

    // Build patient story HTML
    let featuredImageHtml = "";
    if (patientStoryConfig.featuredImageUrl) {
      featuredImageHtml = `
        <div class="patient-story-featured-image">
          <img src="${escapeHtml(patientStoryConfig.featuredImageUrl)}" alt="${escapeHtml(patientStoryConfig.title)}" />
        </div>
      `;
    }

    // QR links to the full story so it can be clicked as well as scanned
    let qrCodeHtml = "";
    if (patientStoryConfig.qrCodeUrl) {
      const qrImageHtml = `<img src="${escapeHtml(patientStoryConfig.qrCodeUrl)}" alt="QR code linking to the full patient story" />`;
      qrCodeHtml = patientStoryConfig.storyUrl
        ? `<a class="patient-story-qr-code" href="${escapeHtml(patientStoryConfig.storyUrl)}" target="_blank" rel="noopener noreferrer" title="Open patient story in a new tab">${qrImageHtml}</a>`
        : `<div class="patient-story-qr-code">${qrImageHtml}</div>`;
    }

    // Image and QR share one row beneath the excerpt so the block stays
    // compact instead of stacking three full-width elements (MCH-211)
    const mediaHtml =
      featuredImageHtml || qrCodeHtml
        ? `<div class="patient-story-media">${featuredImageHtml}${qrCodeHtml}</div>`
        : "";

    patientStoryContent.innerHTML = `
      <div class="patient-story-excerpt">
        ${escapeHtml(patientStoryConfig.excerpt)}
      </div>
      ${mediaHtml}
    `;

    // Assemble patient story container
    patientStoryContainer.appendChild(patientStoryHeader);
    patientStoryContainer.appendChild(patientStoryContent);

    // Replace placeholder or add to canvas
    if (placeholder && placeholder.parentNode) {
      placeholder.parentNode.replaceChild(patientStoryContainer, placeholder);
    } else {
      canvasCharts.appendChild(patientStoryContainer);
    }

    // Sizing needs the story in the document to measure against
    sizePatientStoryMedia(patientStoryContainer);

    // Setup move buttons
    if (window.ReportBuilderUtils?.setupMoveButtons) {
      window.ReportBuilderUtils.setupMoveButtons(
        patientStoryContainer,
        patientStoryConfig.id,
        "data-patient-story-id",
        function () {
          updatePatientStoryArrayOrder(patientStoryConfig.id);
        }
      );
    }

    // Setup edit title button
    if (window.ReportBuilderUtils?.setupEditTitleButton) {
      window.ReportBuilderUtils.setupEditTitleButton(
        patientStoryContainer,
        patientStoryConfig.id,
        "patient-story",
        function (newTitle) {
          patientStoryConfig.customTitle = newTitle;
          // Editing the title flattens it to plain text, so re-apply the link
          wrapPatientStoryTitleLink(patientStoryContainer, patientStoryConfig);
        }
      );
    }

    // Setup remove button
    const removeBtn = patientStoryContainer.querySelector(
      ".btn-remove-patient-story"
    );
    if (removeBtn) {
      removeBtn.addEventListener("click", function () {
        if (window.ReportBuilderUtils?.showConfirmationModal) {
          window.ReportBuilderUtils.showConfirmationModal({
            title: "Remove Patient Story",
            message: "Are you sure you want to remove this patient story? This action cannot be undone.",
            confirmText: "Remove",
            cancelText: "Cancel",
            onConfirm: function() {
              removePatientStory(patientStoryConfig.id);
            }
          });
        } else {
          // Fallback if modal not available
          if (confirm("Are you sure you want to remove this patient story?")) {
            removePatientStory(patientStoryConfig.id);
          }
        }
      });
    }
  }

  /**
   * Update patient story array order to match DOM order
   */
  function updatePatientStoryArrayOrder(patientStoryId) {
    if (window.ReportBuilderUtils?.updateArrayOrder) {
      window.ReportBuilderUtils.updateArrayOrder(
        placedPatientStories,
        patientStoryId,
        "patient_story_container_",
        "data-patient-story-id"
      );
    }
  }

  /**
   * Remove patient story from canvas
   */
  function removePatientStory(patientStoryId) {
    // Remove from placed patient stories array
    placedPatientStories = placedPatientStories.filter(
      (s) => s.id !== patientStoryId
    );

    // Remove from DOM
    const patientStoryContainer = document.getElementById(
      `patient_story_container_${patientStoryId}`
    );
    if (patientStoryContainer) {
      patientStoryContainer.remove();
    }

    // Show empty message if no items left
    const canvasCharts = window.ReportBuilderUtils?.getCanvasContainer();
    if (canvasCharts && canvasCharts.children.length === 0) {
      if (window.ReportBuilderUtils?.toggleEmptyMessage) {
        window.ReportBuilderUtils.toggleEmptyMessage(true);
      }
    }
  }

  // Export functions for external use
  window.ReportBuilderPatientStory = {
    addPatientStoryPlaceholder: addPatientStoryPlaceholder,
    removePatientStory: removePatientStory,
  };

  // Initialize when DOM is ready
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", initPlacePatientStory);
  } else {
    initPlacePatientStory();
  }
})();

